/**
 * ProjectionsSummary Component
 * Summary statistics and key insights for box office projections
 */

import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Assessment,
  Timeline,
  AttachMoney,
  CalendarToday,
  Person
} from '@mui/icons-material';
import { formatCurrency, formatProjectionDate, getProjectionConfidence } from '../../types/boxOfficeTypes';

const ProjectionsSummary = ({ 
  projections, 
  latestProjection, 
  trend, 
  filmTitle 
}) => {
  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!projections || projections.length === 0) return null;

    const totalProjections = projections.length;
    const avgProjection = projections.reduce((sum, p) => sum + (p.actualProjection || 0), 0) / totalProjections;
    const minProjection = Math.min(...projections.map(p => p.actualProjection || 0));
    const maxProjection = Math.max(...projections.map(p => p.actualProjection || 0));
    
    // Calculate trend percentage
    let trendPercentage = 0;
    if (projections.length >= 2) {
      const sortedProjections = [...projections].sort((a, b) => 
        new Date(a.projectionDate) - new Date(b.projectionDate)
      );
      const latest = sortedProjections[sortedProjections.length - 1];
      const previous = sortedProjections[sortedProjections.length - 2];
      trendPercentage = ((latest.actualProjection - previous.actualProjection) / previous.actualProjection) * 100;
    }

    // Calculate confidence distribution
    const confidenceDistribution = projections.reduce((acc, projection) => {
      const confidence = getProjectionConfidence(
        projection.actualProjection,
        projection.outlierMinimum,
        projection.outlierCeiling
      );
      acc[confidence] = (acc[confidence] || 0) + 1;
      return acc;
    }, {});

    // Calculate average days until release
    const avgDaysUntilRelease = projections.reduce((sum, p) => sum + (p.daysUntilRelease || 0), 0) / totalProjections;

    // Get unique creators
    const uniqueCreators = [...new Set(projections.map(p => p.createdBy))].length;

    return {
      totalProjections,
      avgProjection,
      minProjection,
      maxProjection,
      trendPercentage,
      confidenceDistribution,
      avgDaysUntilRelease,
      uniqueCreators
    };
  }, [projections]);

  // Get trend icon and color
  const getTrendInfo = () => {
    switch (trend) {
      case 'up':
        return { 
          icon: <TrendingUp sx={{ color: 'success.main' }} />, 
          color: 'success',
          label: 'Increasing'
        };
      case 'down':
        return { 
          icon: <TrendingDown sx={{ color: 'error.main' }} />, 
          color: 'error',
          label: 'Decreasing'
        };
      default:
        return { 
          icon: <TrendingFlat sx={{ color: 'text.secondary' }} />, 
          color: 'default',
          label: 'Stable'
        };
    }
  };

  const trendInfo = getTrendInfo();

  if (!summaryStats || !latestProjection) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assessment sx={{ color: 'primary.main' }} />
        Projection Summary
      </Typography>

      <Grid container spacing={3}>
        {/* Latest Projection */}
        <Grid item xs={12} md={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Latest Projection
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {formatCurrency(latestProjection.actualProjection)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              {trendInfo.icon}
              <Chip
                label={trendInfo.label}
                size="small"
                color={trendInfo.color}
                variant="outlined"
              />
            </Box>
            {summaryStats.trendPercentage !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {summaryStats.trendPercentage > 0 ? '+' : ''}{summaryStats.trendPercentage.toFixed(1)}% from previous
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Projection Range */}
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Projection Range
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Minimum:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(summaryStats.minProjection)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Average:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(summaryStats.avgProjection)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Maximum:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(summaryStats.maxProjection)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Confidence Distribution */}
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Confidence Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(summaryStats.confidenceDistribution).map(([confidence, count]) => {
                const percentage = (count / summaryStats.totalProjections) * 100;
                const getConfidenceColor = () => {
                  switch (confidence) {
                    case 'high': return 'success';
                    case 'medium': return 'warning';
                    case 'low': return 'error';
                    default: return 'default';
                  }
                };
                return (
                  <Box key={confidence}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                        {confidence} Confidence
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {count} ({percentage.toFixed(0)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      color={getConfidenceColor()}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Grid>

        {/* Timeline & Metadata */}
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Timeline & Metadata
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Avg Days to Release</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {Math.round(summaryStats.avgDaysUntilRelease)} days
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Unique Creators</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {summaryStats.uniqueCreators}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Latest Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatProjectionDate(latestProjection.projectionDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Quick Insights */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Quick Insights
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`${summaryStats.totalProjections} Total Projections`}
            size="small"
            variant="outlined"
            icon={<Assessment />}
          />
          <Chip
            label={`Range: ${formatCurrency(summaryStats.maxProjection - summaryStats.minProjection)}`}
            size="small"
            variant="outlined"
            icon={<AttachMoney />}
          />
          {summaryStats.trendPercentage !== 0 && (
            <Chip
              label={`${summaryStats.trendPercentage > 0 ? '+' : ''}${summaryStats.trendPercentage.toFixed(1)}% Trend`}
              size="small"
              color={trendInfo.color}
              variant="outlined"
              icon={trendInfo.icon}
            />
          )}
          <Chip
            label={`${summaryStats.uniqueCreators} Contributors`}
            size="small"
            variant="outlined"
            icon={<Person />}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ProjectionsSummary;
